import { Fragment, useState } from 'react';
import { Eye, ChevronRight, ChevronDown, X } from 'lucide-react';
import { PeriodSelector, validatePeriodMax12Months, type PeriodValue } from '@/shared/components/PeriodSelector.tsx';
import { DownloadButton } from '@/shared/components/DownloadButton.tsx';
import { useAuditReport, useAuditFilters } from '../hooks/useReports.ts';
import type { AuditParams } from '../api/reports.api.ts';

const fmtDateTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function fmtJson(val: unknown): string {
  if (val === null || val === undefined) return '—';
  try {
    return JSON.stringify(val, null, 2);
  } catch {
    return String(val);
  }
}

interface TabProps { availableMonths?: string[] }

export function AuditTab({ availableMonths }: TabProps) {
  const MONTH_NOW = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [period, setPeriod] = useState<PeriodValue>({ mode: 'month', month: MONTH_NOW });
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [queryParams, setQueryParams] = useState<AuditParams | null>(null);
  const [periodError, setPeriodError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { data: filters } = useAuditFilters();
  const { data, isLoading } = useAuditReport(queryParams);

  function handleVisualize() {
    setPeriodError('');
    setExpandedRows(new Set());

    if (period.mode === 'month') {
      if (!period.month) { setPeriodError('Selecione um mes'); return; }
      const [y, m] = period.month.split('-').map(Number);
      const from = new Date(y, m - 1, 1).toISOString();
      const to = new Date(y, m, 0, 23, 59, 59).toISOString();
      setQueryParams({ from, to, userId: userFilter || undefined, action: actionFilter || undefined });
    } else {
      const err = validatePeriodMax12Months(period);
      if (err) { setPeriodError(err); return; }
      if (!period.from || !period.to) { setPeriodError('Selecione o intervalo completo'); return; }
      setQueryParams({
        from: new Date(period.from).toISOString(),
        to: new Date(period.to).toISOString(),
        userId: userFilter || undefined,
        action: actionFilter || undefined,
      });
    }
  }

  function toggleRow(idx: number) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  const downloadParams: Record<string, string | undefined> = queryParams
    ? { from: queryParams.from, to: queryParams.to, userId: queryParams.userId, action: queryParams.action }
    : {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 pb-2">
        <div className="relative pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Período</label>
          <PeriodSelector value={period} onChange={setPeriod} modes={['month', 'range']} availableMonths={availableMonths} error={periodError} />
        </div>
        <div className="pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Usuário</label>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Todos</option>
            {filters?.users.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
        </div>
        <div className="pb-4">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Ação</label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">Todas</option>
            {filters?.actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="pb-4">
          <button
            onClick={handleVisualize}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Eye size={14} />
            {isLoading ? 'Carregando...' : 'Visualizar'}
          </button>
        </div>
        <div className="pb-4">
          <DownloadButton
            url="/admin/reports/audit/pdf"
            params={downloadParams}
            filename="R003-auditoria.pdf"
            label="Exportar PDF"
            disabled={!queryParams}
          />
        </div>
        {/* SEM botão CSV — dado sensível, apenas PDF */}
        {(userFilter || actionFilter || queryParams) && (
          <div className="pb-4">
            <button
              onClick={() => {
                setUserFilter('');
                setActionFilter('');
                setPeriod({ mode: 'month', month: MONTH_NOW });
                setQueryParams(null);
                setPeriodError('');
                setExpandedRows(new Set());
              }}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              <X size={14} />
              Limpar
            </button>
          </div>
        )}
      </div>

      {queryParams && data && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          {data.length === 0 ? (
            <div className="flex h-48 items-center justify-center text-sm text-gray-400">
              Nenhum dado para o período selecionado.
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="w-8 px-2 py-3" />
                  <th className="px-4 py-3">Usuário</th>
                  <th className="px-4 py-3">Ação</th>
                  <th className="px-4 py-3">Entidade</th>
                  <th className="px-4 py-3">ID Entidade</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, idx) => {
                  const isOpen = expandedRows.has(idx);
                  const hasDetail = !!(row.dadosAnteriores || row.dadosNovos);
                  return (
                    <Fragment key={idx}>
                      <tr className="border-b border-gray-100 dark:border-gray-800">
                        <td className="px-2 py-3">
                          {hasDetail && (
                            <button onClick={() => toggleRow(idx)} className="text-gray-400 hover:text-gray-600">
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3">{row.adminNome}</td>
                        <td className="px-4 py-3">{row.acao}</td>
                        <td className="px-4 py-3">{row.entidade}</td>
                        <td className="px-4 py-3">
                          {row.entidadeId
                            ? <span className="font-mono text-xs">{row.entidadeId.slice(0, 8)}...</span>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{row.ip ?? '—'}</td>
                        <td className="px-4 py-3 text-xs">{fmtDateTime.format(new Date(row.createdAt))}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="mb-1 text-xs font-semibold text-gray-500">Dados Anteriores</p>
                                <pre className="max-h-48 overflow-auto rounded bg-white p-2 text-xs dark:bg-gray-900">
                                  {fmtJson(row.dadosAnteriores)}
                                </pre>
                              </div>
                              <div>
                                <p className="mb-1 text-xs font-semibold text-gray-500">Dados Novos</p>
                                <pre className="max-h-48 overflow-auto rounded bg-white p-2 text-xs dark:bg-gray-900">
                                  {fmtJson(row.dadosNovos)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
